import React from 'react';
import { Box, Button, Typography, Tooltip, useTheme, alpha } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteForeverRounded from '@mui/icons-material/DeleteForeverRounded';
import { useDispatch, useSelector } from 'react-redux';
import { UnknownAction } from '@reduxjs/toolkit';
import ReducersIndex from 'reduxModules/ducks';
import { RootState } from 'types/redux/state';
import { maxExpansionMicCountFor, maxExpansionSpeakerCountFor } from 'toolConstants/conferenceroomDesigner/treeFrogPorts';

/**
 * Menu-only Add / Remove button for expansion devices. Used in both the microphones
 * menu and the speakers menu — the `device` prop switches which state field is read
 * and which modal opens. Clicking just dispatches an action; the rest of the flow
 * (Add → 'ceiling', No thanks → 'none') is handled by `ExpansionDialog`, which
 * covers both the mic and speaker add modals.
 *
 * For microphones: shown when the user has a non-CBC bar mic and lets them request
 * expansion mics even after dismissing the auto-prompted dialog. Active types are
 * 'ceiling' and 'table'.
 *
 * For speakers: shown when the user has a bar speaker and lets them request expansion
 * speakers manually. Active types are 'ceiling' and 'pendant'.
 *
 * Renders a labelled section header above the button so the control reads as a
 * labelled section rather than an inline labelled input.
 */
type ExpansionDeviceKind = 'microphones' | 'speakers';

const DEVICE_CONFIG: Record<ExpansionDeviceKind, {
  header: string;
  removeLabel: string;
  addModalName: string;
}> = {
  microphones: {
    header: 'Expansion Microphones',
    removeLabel: 'Remove expansion microphones',
    addModalName: 'expansionMicAdd',
  },
  speakers: {
    header: 'Expansion Speakers',
    removeLabel: 'Remove expansion speakers',
    addModalName: 'expansionSpeakerAdd',
  },
};

const ExpansionAddButton: React.FC<{ label?: string; device?: ExpansionDeviceKind }> = ({ label, device = 'microphones' }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const config = DEVICE_CONFIG[device];

  // Reads `expansionMicType` for the microphones flavour and `expansionSpeakerType`
  // for the speakers flavour. Both are nullable strings on the area.
  const expansionType = useSelector((state: RootState) => {
    const area = state.container.areas[state.container.activeTab];
    if (!area) return null;
    return device === 'microphones'
      ? area.expansionMicType ?? null
      : area.expansionSpeakerType ?? null;
  });

  // The expansion placement can run and DECLINE, when the coverage gap the bar leaves is below
  // the minimum worth a mic (spec edge case B / below-minimum-viable Scenario 3). The type stays
  // 'ceiling'/'table' through that — the config is still meaningful and the placement restores the
  // mics once acoustics move back to a tier with a worthwhile gap — but there is nothing on the
  // canvas, so offering "Remove expansion microphones" would be a destructive-looking button for
  // mics that aren't there. Treated as "no expansion" here so the control offers "Add" instead.
  //
  // Read from the flag rather than from `layout.length`: an empty layout ALSO describes the normal
  // transient right after `setExpansionMicType`, which seeds `{ qty: 0, layout: [] }` before the
  // middleware places, so keying on length would flicker this button on every add.
  const isSuppressed = useSelector((state: RootState) => {
    if (device !== 'microphones') return false;
    const area = state.container.areas[state.container.activeTab];
    return Boolean(area?.expansionMicSuppressed);
  });

  // Mics: 'ceiling' | 'table'. Speakers: 'ceiling' | 'pendant'. Either way, the
  // remove/add toggle is keyed off "anything truthy that isn't 'none' or null".
  const hasExpansion = (expansionType === 'ceiling'
    || expansionType === 'table'
    || expansionType === 'pendant')
    && !isSuppressed;

  // TC-05: Tesira has no bar + expansion equivalent, so if the OTHER expansion
  // slot (mics vs speakers) already saturates the UCC's 2-port budget, opening
  // this Add dialog would only let the user click through it and land on 0
  // placed devices (the placement reducers cap at the port budget too — see
  // treeFrogPorts.js). Disable Add outright in that case instead of letting
  // the user try. Only relevant in Add mode — Remove never overflows.
  const canAddAtLeastOne = useSelector((state: RootState) => {
    if (hasExpansion) return true;
    const area = state.container.areas[state.container.activeTab];
    if (!area) return true;
    const pseudoArea = {
      microphones: area.microphones,
      expansionMicType: area.expansionMicType,
      expansionMicrophones: area.expansionMicrophones,
      expansionSpeakerType: area.expansionSpeakerType,
      expansionSpeakers: area.expansionSpeakers,
    };
    return device === 'microphones'
      ? maxExpansionMicCountFor(pseudoArea, 1) >= 1
      : maxExpansionSpeakerCountFor(pseudoArea, 1) >= 1;
  });

  const onClick = () => {
    if (hasExpansion) {
      // Remove — clears the area's expansion slot. setExpansion*Type('none') drops
      // the layout via the reducer; the recalc middleware then removes the rendered
      // devices from the canvas.
      const setTypeAction = device === 'microphones'
        ? ReducersIndex.container.setExpansionMicType('none' as any)
        : ReducersIndex.container.setExpansionSpeakerType('none' as any);
      dispatch(setTypeAction as unknown as UnknownAction);
    } else {
      // Manual "Add" entry — opens the add variant of the dialog (shorter copy,
      // "Add expansion {mics,speakers}" title). The auto-recommendation flow uses
      // `expansion{Mic,Speaker}Recommendation` instead and is dispatched from middleware.
      dispatch(ReducersIndex.uiReducers.openModal(config.addModalName) as unknown as UnknownAction);
    }
  };

  const buttonLabel = hasExpansion
    ? config.removeLabel
    : (label ?? 'Add');

  const icon = hasExpansion ? <DeleteForeverRounded /> : <AddIcon />;

  // Route colors through the theme so dark mode and theme overrides apply. Remove uses
  // `error.main` (destructive action); add uses `text.primary` for a neutral outlined look.
  const accentColor = hasExpansion ? theme.palette.error.main : theme.palette.text.primary;

  const addDisabled = !hasExpansion && !canAddAtLeastOne;

  const button = (
    <Button
      variant="outlined"
      onClick={onClick}
      disabled={addDisabled}
      fullWidth
      startIcon={icon}
      sx={{
        fontFamily: 'Open Sans',
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '12px',
        height: '40px',
        padding: '5px',
        color: accentColor,
        borderColor: accentColor,
        '&:hover': {
          borderColor: accentColor,
          backgroundColor: hasExpansion
            ? alpha(theme.palette.error.main, 0.08)
            : theme.palette.action.hover,
        },
      }}
    >
      {buttonLabel}
    </Button>
  );

  return (
    <Box sx={{ width: '100%' }}>
      <Typography
        variant="body1"
        sx={{
          mb: 1,
          fontFamily: 'Open Sans',
          fontWeight: 600,
          fontSize: '12px',
        }}
      >
        {config.header}
      </Typography>
      {addDisabled ? (
        <Tooltip title="No UCC peripheral ports left — reduce expansion microphones/speakers to free one up.">
          {/* span wrapper: MUI Tooltip needs a listenable child, which a disabled Button isn't */}
          <span>{button}</span>
        </Tooltip>
      ) : button}
    </Box>
  );
};

export default ExpansionAddButton;
